import { site } from './documentation.config.tsx';
import type { Page, PageGroup } from './page.ts';

import { isGeneratedPath } from '#src/utils/path.ts';

/**
 * The flattened page tree.
 *
 * This is deliberately kept free of `site.map.ts` and any page components so
 * that lightweight consumers (e.g. tracking) can look up page metadata without
 * importing every page in the site, which would create import cycles.
 */

export interface PageNavigation {
  path: string;
  label: string;
  parent?: string;
}

export type FlatPage = Omit<Page, 'children'> & {
  parent?: {
    path?: string;
    label?: string;
  };
  prev?: PageNavigation;
  next?: PageNavigation;

  /**
   * Whether the page was directly annotated with `hidden: true`.
   *
   * The idea here is to only mark the topmost page in the tree as hidden then
   * enable navigation amongst its subtree pages without cross references to
   * unrelated hidden subtrees.
   */
  hiddenDirectly?: true;
};

export type RenderablePage = Omit<Page, 'path' | 'type'> & {
  path: string;
  type: NonNullable<Page['type']>;
};

const flattenPages = (pages: PageGroup[], parentHidden = false): FlatPage[] =>
  pages.reduce<FlatPage[]>((acc, { children, ...page }) => {
    if ('type' in page) {
      acc.push({
        ...page,
        ...(parentHidden || page.hidden ? { hidden: true } : {}),
        ...(page.hidden ? { hiddenDirectly: true } : {}),
      });
    }

    if (children !== undefined) {
      const newChildren = children.map((child) => ({
        ...child,
        parent: { path: page.path, label: page.label },
      }));
      acc.push(...flattenPages(newChildren, parentHidden || page.hidden));
    }

    return acc;
  }, []);

const isRenderablePage = (page: FlatPage): page is RenderablePage & FlatPage =>
  page.path !== undefined &&
  page.path.startsWith('/') &&
  page.type !== undefined;

/**
 * Check whether a pages path is part of the generated documentation
 */
export const isGeneratedPage = (page: FlatPage): boolean =>
  isGeneratedPath(page.path);

export const RENDERABLE_PAGES = Object.fromEntries(
  flattenPages(site)
    .filter(isRenderablePage)
    .filter(isGeneratedPage)
    .map((page, index, arr) => {
      let nextReset = false;
      const next = arr.slice(index + 1).find((p) => {
        if (!p.hidden) {
          return true;
        }

        if (p.hiddenDirectly) {
          // We've reached a different hidden subtree.
          // Ignore subsequent hidden pages.
          nextReset = true;
          return false;
        }

        return !nextReset && page.hidden;
      });

      let prevReset = page.hiddenDirectly;
      const prev = arr
        .slice(0, index)
        .reverse()
        .find((p) => {
          if (!p.hidden) {
            return true;
          }

          if (p.hiddenDirectly) {
            // We've reached the topmost page in our hidden subtree.
            // Ignore previous hidden pages that belong to a different subtree.
            prevReset = true;
            // But allow linking back to our own parent.
            return p.path === page.parent?.path;
          }

          return !prevReset && page.hidden;
        });

      return [
        page.path,
        {
          ...page,
          prev: prev
            ? {
                path: prev.path,
                label: prev.label,
                parent: prev.parent?.label,
              }
            : undefined,
          next: next
            ? {
                path: next.path,
                label: next.label,
                parent: next.parent?.label,
              }
            : undefined,
        },
      ];
    }),
);
